/**
 * Base de datos local (IndexedDB vía Dexie).
 *
 * Esta es la fuente de verdad del dispositivo. La app escribe AQUÍ y responde
 * de inmediato; la subida a Google Sheets ocurre después, en segundo plano.
 * Nunca se espera a la red para guardar algo.
 *
 * ── CÓMO SE MODELA EL DINERO ────────────────────────────────────────────────
 *
 * Todo movimiento mueve plata de un lado a otro:
 *
 *   ingreso        →  entra a `cuenta_destino`        (origen vacío)
 *   egreso         →  sale de `cuenta_origen`         (destino vacío)
 *   transferencia  →  sale de `origen`, entra a `destino`
 *   ajuste         →  corrige un saldo contra la realidad
 *
 * El saldo de una cuenta es:
 *      suma de lo que entró  −  suma de lo que salió
 *
 * Por eso una transferencia de Banco de Bogotá a Nequi NO te empobrece:
 * resta en una cuenta y suma en la otra, y el patrimonio queda igual.
 * Este es el error clásico de las apps de finanzas y la razón de que este
 * modelo tenga dos columnas de cuenta en vez de una.
 *
 * ── EL 4x1000 ───────────────────────────────────────────────────────────────
 *
 * El GMF se guarda como un movimiento HIJO, no como una columna del padre.
 * Cuesta una fila más, pero a cambio: el saldo queda bien sin lógica especial,
 * el 4x1000 aparece solo en el informe anual de comisiones, y se puede editar
 * o borrar sin tocar el movimiento original.
 *
 * ── SINCRONIZACIÓN ──────────────────────────────────────────────────────────
 *
 * Toda tabla que se sincroniza lleva cuatro columnas de control:
 *   id            UUIDv7 generado aquí. Ordena por tiempo y hace que reenviar
 *                 un movimiento lo actualice en vez de duplicarlo.
 *   actualizado_en  milisegundos. Decide quién gana si hubo edición en los dos
 *                 dispositivos: gana el más reciente.
 *   borrado_en    borrado suave. Si se borrara la fila de verdad, el otro
 *                 dispositivo no tendría cómo enterarse y la resucitaría.
 *   dispositivo   quién hizo el cambio. Desempata y evita que el servidor te
 *                 devuelva lo que tú mismo acabas de mandar.
 */

import Dexie from 'dexie';

export const VERSION_ESQUEMA = 5;   // v2: contratos · v3: abonos · v4: índice de cuotas · v5: índice de suscripciones

/** Tablas que viajan a Google Sheets. `outbox` y `adjuntos` se quedan aquí. */
export const TABLAS_SINCRONIZADAS = [
  'cuentas', 'movimientos', 'lineas', 'categorias', 'presupuestos',
  'deudas', 'abonos', 'tarjetas', 'compras_diferidas', 'eventos', 'recurrentes',
  'reglas', 'entidades', 'contratos', 'ajustes',
];

// ─── Vocabulario cerrado ────────────────────────────────────────────────────
// Se declara aquí para que la app entera hable el mismo idioma y para que un
// error de dedo no cree una categoría fantasma llamada "Egreso" con mayúscula.

export const TIPO_MOVIMIENTO = {
  INGRESO: 'ingreso',
  EGRESO: 'egreso',
  TRANSFERENCIA: 'transferencia',
  AJUSTE: 'ajuste',
};

export const TIPO_CUENTA = {
  EFECTIVO: 'efectivo',
  AHORROS: 'ahorros',
  CORRIENTE: 'corriente',
  BILLETERA: 'billetera',          // Nequi, Daviplata, Movii, RappiPay
  TARJETA_CREDITO: 'tarjeta_credito',  // es un PASIVO: saldo negativo = deuda
  CREDITO_ROTATIVO: 'credito_rotativo', // Addi, Sistecrédito, Sumas
  PRESTAMO: 'prestamo',
  INVERSION: 'inversion',          // CDT, fondo, acciones, cripto
  POR_COBRAR: 'por_cobrar',        // plata que te deben
  POR_PAGAR: 'por_pagar',          // fiado, plata que debes a una persona
};

/** Cuentas que representan deuda: su saldo cuenta negativo en el patrimonio. */
export const CUENTAS_PASIVO = [
  TIPO_CUENTA.TARJETA_CREDITO,
  TIPO_CUENTA.CREDITO_ROTATIVO,
  TIPO_CUENTA.PRESTAMO,
  TIPO_CUENTA.POR_PAGAR,
];

/**
 * Cuentas con cupo que se repone a medida que pagas.
 *
 * ── LA DIFERENCIA ENTRE UNA TARJETA Y ADDI ──────────────────────────────────
 *
 * Parecen lo mismo pero se pagan distinto, y confundirlas hace que la app te
 * diga mal cuánto debes este mes:
 *
 *   TARJETA DE CRÉDITO (Nu, Visa, Mastercard)
 *     Todas las compras del periodo caen en UNA sola factura mensual.
 *     Hay una fecha de corte y una fecha límite de pago, iguales para todo.
 *     En Nu el periodo de gracia entre corte y límite es de 10 días, y puedes
 *     elegir tu fecha de pago.
 *
 *   CRÉDITO ROTATIVO POR COMPRA (Addi, Sistecrédito)
 *     CADA COMPRA es su propio crédito, con su propio plan de cuotas, su
 *     propia tasa y sus propias fechas. Comprar el 3 y comprar el 20 genera
 *     dos calendarios distintos. No hay fecha de corte global.
 *     En Addi la tasa va de 0% E.A. (las promociones de 3 cuotas) hasta
 *     28,69% E.A. en consumo, y hasta 62,56% E.A. en créditos de bajo monto
 *     (cifras de julio de 2026 — confirmar antes de usarlas para calcular).
 *     El cupo se repone a medida que pagas, y el prepago no tiene penalidad.
 *
 * Por eso `compras_diferidas` cuelga de la CUENTA y no de la tarjeta, y por
 * eso cada compra lleva su propia fecha de primera cuota.
 */
export const CUENTAS_CON_CUPO = [
  TIPO_CUENTA.TARJETA_CREDITO,
  TIPO_CUENTA.CREDITO_ROTATIVO,
];

export const METODO_PAGO = {
  EFECTIVO: 'efectivo',
  TRANSFERENCIA: 'transferencia',
  QR: 'qr',
  PSE: 'pse',
  DEBITO: 'debito',
  CREDITO: 'credito',
  DEBITO_AUTOMATICO: 'debito_automatico',
  DATAFONO: 'datafono',
  LINK_PAGO: 'link_pago',
  CORRESPONSAL: 'corresponsal',
  OTRO: 'otro',
};

/** De dónde salió el dato. Sirve para auditar lo que metió la IA. */
export const ORIGEN_DATO = {
  MANUAL: 'manual',
  OCR: 'ocr',            // foto leída por Gemini
  VOZ: 'voz',
  TEXTO_LIBRE: 'texto_libre',
  RECURRENTE: 'recurrente',
  IMPORTADO: 'importado',
  AUTOMATICO: 'automatico',   // el GMF que genera la app
};

/** Para la regla 50/30/20. */
export const NATURALEZA = {
  NECESIDAD: 'necesidad',
  DESEO: 'deseo',
  AHORRO_DEUDA: 'ahorro_deuda',
};

// ─── Esquema ────────────────────────────────────────────────────────────────
//
// En Dexie solo se declaran los campos que se INDEXAN (los que se buscan o se
// ordenan). El resto del objeto se guarda igual. La lista completa de campos
// de cada tabla está documentada abajo, en PLANTILLAS.

export const db = new Dexie('educacion_financiera');

db.version(VERSION_ESQUEMA).stores({
  cuentas: 'id, nombre, tipo, entidad_id, archivada, actualizado_en, borrado_en',

  // Los índices compuestos son los que hacen que la lista de movimientos
  // cargue rápido cuando ya haya miles de filas.
  // `compra_diferida_id` se indexa porque borrar una compra a cuotas tiene
  // que encontrar sus movimientos. Sin índice, Dexie no busca: lanza error.
  movimientos: `id, fecha, tipo, cuenta_origen, cuenta_destino, categoria_id,
                evento_id, deuda_id, tarjeta_id, movimiento_padre_id,
                compra_diferida_id, recurrente_id, actualizado_en, borrado_en,
                [fecha+tipo], [cuenta_origen+fecha], [cuenta_destino+fecha],
                [categoria_id+fecha]`,

  lineas: 'id, movimiento_id, categoria_id, actualizado_en, borrado_en',
  categorias: 'id, nombre, padre_id, tipo, naturaleza, actualizado_en, borrado_en',
  presupuestos: 'id, periodo, categoria_id, [periodo+categoria_id], actualizado_en, borrado_en',
  deudas: 'id, acreedor, activa, estado, proxima_fecha_pago, actualizado_en, borrado_en',

  // Cada pago que se hizo o se recibió, con su fecha y su saldo. Es el
  // historial: sin él solo se sabe cuánto falta, nunca qué pasó.
  abonos: `id, deuda_id, compra_id, fecha, clase, actualizado_en, borrado_en,
           [deuda_id+fecha], [compra_id+fecha]`,

  tarjetas: 'id, cuenta_id, banco, dia_corte, dia_limite_pago, actualizado_en, borrado_en',
  compras_diferidas: `id, cuenta_id, fecha_compra, fecha_proxima_cuota,
                      cuotas_pendientes, pagada, actualizado_en, borrado_en`,
  eventos: 'id, fecha_inicio, fecha_fin, actualizado_en, borrado_en',
  recurrentes: 'id, proxima_fecha, activa, cobro, actualizado_en, borrado_en',
  reglas: 'id, patron, categoria_id, actualizado_en, borrado_en',
  entidades: 'id, nombre, tipo, actualizado_en, borrado_en',
  contratos: 'id, contratante, fecha_inicio, fecha_fin, activo, actualizado_en, borrado_en',
  ajustes: 'id, clave, actualizado_en, borrado_en',

  // ── Locales: no viajan a Sheets ──────────────────────────────────────────
  // La cola de subida. Vive y muere en este dispositivo.
  outbox: '++seq, tabla, registro_id, intentos, proximo_en',
  // Las fotos. Pesan mucho para una hoja de cálculo; se quedan aquí.
  adjuntos: 'id, movimiento_id, creado_en',
  // Los mensajes de coaching ya mostrados, para no repetirlos.
  alertas: 'id, tipo, creado_en, vista, descartada',
});

/**
 * PLANTILLAS — la lista completa de campos por tabla.
 *
 * Esto no es decoración: es el contrato. Si un campo no está aquí, no existe.
 * Cada `crear*` devuelve un objeto con todos los campos en su valor por
 * defecto, para que nunca se guarde un registro a medias.
 */

/** Identificador ordenado por tiempo. Ver nota en TABLAS_SINCRONIZADAS. */
export function nuevoId() {
  // UUIDv7: 48 bits de milisegundos + aleatorio. Al ordenar por id, quedan
  // en orden cronológico, que es justo como se leen los movimientos.
  const ms = Date.now();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[0] = (ms / 2 ** 40) & 0xff;
  bytes[1] = (ms / 2 ** 32) & 0xff;
  bytes[2] = (ms / 2 ** 24) & 0xff;
  bytes[3] = (ms / 2 ** 16) & 0xff;
  bytes[4] = (ms / 2 ** 8) & 0xff;
  bytes[5] = ms & 0xff;
  bytes[6] = (bytes[6] & 0x0f) | 0x70;   // versión 7
  bytes[8] = (bytes[8] & 0x3f) | 0x80;   // variante RFC 4122
  const h = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

/** Identificador de este dispositivo. Se crea una vez y se guarda. */
export function idDispositivo() {
  let id = localStorage.getItem('id_dispositivo');
  if (!id) {
    id = nuevoId().slice(0, 8);
    localStorage.setItem('id_dispositivo', id);
  }
  return id;
}

function control() {
  return {
    id: nuevoId(),
    actualizado_en: Date.now(),
    borrado_en: null,
    dispositivo: idDispositivo(),
  };
}

export function crearCuenta(datos = {}) {
  return {
    ...control(),
    nombre: '',                 // "Nequi", "Bolsillo", "Bogotá ahorros"
    tipo: TIPO_CUENTA.EFECTIVO,
    entidad_id: null,           // → entidades
    moneda: 'COP',
    saldo_inicial: 0,           // entero, en pesos
    saldo_reportado: null,      // lo que dice el banco, para conciliar
    fecha_saldo_reportado: null,

    // Solo para cuentas con cupo (tarjeta de crédito, Addi, Sistecrédito).
    // El cupo disponible NO se guarda: se calcula como cupo_total menos lo
    // que debes. Guardarlo sería tener dos verdades que se pueden contradecir.
    cupo_total: 0,
    alerta_cupo_pct: 70,        // avísame si paso de este % de uso
    exenta_gmf: false,          // la cuenta marcada como exenta del 4x1000
    incluir_en_patrimonio: true, // apagar para plata de terceros
    color: null,
    icono: null,
    orden: 0,
    archivada: false,
    notas: '',
    ...datos,
  };
}

export function crearMovimiento(datos = {}) {
  return {
    ...control(),

    // ── Lo esencial ──
    tipo: TIPO_MOVIMIENTO.EGRESO,
    monto: 0,                   // SIEMPRE positivo y entero. El signo lo da el tipo.
    fecha: hoy(),               // 'YYYY-MM-DD'
    hora: null,                 // 'HH:MM'

    // ── De dónde y hacia dónde ──
    cuenta_origen: null,        // sale de aquí  (egreso, transferencia)
    cuenta_destino: null,       // entra aquí    (ingreso, transferencia)

    // ── Qué fue ──
    categoria_id: null,         // no aplica a transferencias entre cuentas propias
    concepto: '',               // lo que el usuario escribe
    contraparte: '',            // "Terpel", "Éxito", "Juan"
    metodo_pago: METODO_PAGO.EFECTIVO,

    // ── Impuestos y costos ──
    gmf: 0,                     // informativo; el cobro real va como movimiento hijo
    movimiento_padre_id: null,  // si este movimiento ES el cobro del 4x1000
    impuestos: 0,               // IVA, INC del 8% en restaurantes
    propina: 0,

    // ── Enlaces ──
    evento_id: null,            // → eventos (el viaje, la reunión)
    deuda_id: null,             // si es el pago de una cuota
    tarjeta_id: null,           // si se pagó con tarjeta de crédito
    compra_diferida_id: null,   // si es la cuota de una compra a plazos
    recurrente_id: null,        // si lo generó una plantilla
    adjunto_id: null,           // la foto del comprobante

    // ── Banderas ──
    reembolsable: false,        // te lo van a devolver
    reembolsado: false,
    deducible: false,           // para la declaración de renta
    conciliado: false,          // ya lo verificaste contra el extracto
    dividido: false,            // tiene líneas en la tabla `lineas`

    // ── Procedencia y confianza ──
    origen_dato: ORIGEN_DATO.MANUAL,
    confianza_ocr: null,        // objeto con la confianza por campo
    revisado: true,             // lo que trae la IA entra en false hasta que lo apruebes
    referencia: '',             // número de comprobante del banco
    etiquetas: [],
    notas: '',
    ...datos,
  };
}

/** Una porción de un movimiento dividido en varias categorías. */
export function crearLinea(datos = {}) {
  return {
    ...control(),
    movimiento_id: null,
    categoria_id: null,
    monto: 0,
    concepto: '',
    ...datos,
  };
}

export function crearCategoria(datos = {}) {
  return {
    ...control(),
    nombre: '',
    padre_id: null,             // jerarquía de dos niveles
    tipo: 'egreso',             // 'ingreso' | 'egreso'
    naturaleza: NATURALEZA.NECESIDAD,   // para la regla 50/30/20
    color: null,
    icono: null,
    orden: 0,
    archivada: false,
    ...datos,
  };
}

export function crearPresupuesto(datos = {}) {
  return {
    ...control(),
    periodo: mesActual(),       // 'YYYY-MM'
    categoria_id: null,
    monto_planeado: 0,
    arrastrar_sobrante: false,  // lo que sobra pasa al mes siguiente
    sobrante_anterior: 0,
    ...datos,
  };
}

/**
 * Configuración EXTRA de una tarjeta de crédito de verdad.
 *
 * Solo existe para cuentas tipo `tarjeta_credito`. Addi y Sistecrédito no
 * tienen fila aquí, porque no tienen fecha de corte: cada compra suya trae su
 * propio calendario. El cupo vive en la cuenta, no aquí, porque lo tienen las
 * dos clases de crédito.
 */
export function crearTarjeta(datos = {}) {
  return {
    ...control(),
    cuenta_id: null,            // → cuentas (tipo tarjeta_credito)
    banco: '',
    franquicia: '',             // Visa, Mastercard, Amex
    ultimos_4: '',
    cupo_avances: 0,

    // Las dos fechas que la gente confunde, y que aquí van separadas.
    // El "periodo de gracia" son los días entre una y otra: en Nu son 10.
    // Comprar justo DESPUÉS del corte te da casi un mes más para pagar.
    dia_corte: 1,               // día en que se cierra el periodo
    dia_limite_pago: 11,        // día hasta el que puedes pagar sin mora

    tasa_ea: null,              // tasa efectiva anual, en %
    cuota_manejo: 0,
    seguros: 0,
    activa: true,
    notas: '',
    ...datos,
  };
}

/**
 * Una compra que se difirió a varias cuotas.
 *
 * Sirve igual para una compra diferida de la tarjeta de Nu y para una compra
 * con Addi. La diferencia está en de qué cuenta cuelga y en si tiene fechas
 * propias — ver la nota de CUENTAS_CON_CUPO.
 */
export function crearCompraDiferida(datos = {}) {
  return {
    ...control(),
    cuenta_id: null,            // → cuentas (la tarjeta o el crédito rotativo)
    movimiento_id: null,        // la compra original
    descripcion: '',
    comercio: '',
    monto_total: 0,
    cuotas_totales: 1,
    cuotas_pagadas: 0,
    cuotas_pendientes: 1,
    valor_cuota: 0,             // calculado con repartir(), sin perder pesos
    saldo_pendiente: 0,

    // Intereses
    tasa_ea: null,
    sin_intereses: false,       // las promos tipo "3 cuotas a 0%" de Addi
    total_intereses: 0,         // cuánto vas a pagar de más por diferir

    // Calendario PROPIO de esta compra.
    // En una tarjeta lo manda la fecha de corte de la cuenta y esto va vacío.
    // En Addi cada compra trae el suyo, así que aquí sí se llena.
    fecha_compra: hoy(),
    fecha_primera_cuota: null,  // 'YYYY-MM-DD'
    dia_pago: null,             // día del mes en que cae cada cuota
    fecha_proxima_cuota: null,  // se recalcula al pagar
    fecha_ultima_cuota: null,   // cuándo terminas de pagar esto

    /**
     * El calendario, cuota por cuota.
     *
     * Normalmente va en null y se calcula solo con planDeCuotas(): tener una
     * sola fuente de verdad evita que el plan guardado y los parámetros se
     * contradigan. Solo se llena cuando el usuario corrige una fecha o un
     * valor a mano, porque la entidad le puso otra cosa. Ahí el plan guardado
     * manda, que es lo correcto: la app no le va a discutir a Addi.
     */
    plan: null,

    tarjeta_id: null,           // → tarjetas, para tomar la fecha de corte
    es_avance: false,           // los avances generan intereses desde el día 1
    pagada: false,
    ...datos,
  };
}

/**
 * Cómo puede terminar una deuda.
 *
 * ── POR QUÉ NO BASTA CON "ACTIVA: SÍ / NO" ──────────────────────────────────
 *
 * Porque en la vida real una deuda entre personas no siempre se paga ni
 * siempre se deja de deber. Termina de cuatro formas distintas, y cada una
 * le hace algo diferente a tu plata:
 *
 *   SALDADA      te pagaron completo. La plata volvió. No ganaste ni perdiste.
 *   CONDONADA    dijiste "déjalo así". Ahí sí perdiste esa plata de verdad:
 *                es el único caso en que tu patrimonio baja, y debe bajar.
 *   INCOBRABLE   no perdonaste: te resignaste. Contablemente es lo mismo que
 *                condonar, pero se distingue porque no es lo mismo en la
 *                cabeza de uno, y porque a esa persona uno no le vuelve a
 *                prestar.
 *   ACTIVA       sigue viva, con o sin abonos encima.
 *
 * Al revés funciona igual: si TE perdonaron lo que debías, eso es plata que
 * te entró. Tu patrimonio sube el día que te la perdonan.
 */
export const ESTADO_DEUDA = {
  ACTIVA: 'activa',
  SALDADA: 'saldada',
  CONDONADA: 'condonada',
  INCOBRABLE: 'incobrable',
};

export function crearDeuda(datos = {}) {
  return {
    ...control(),
    acreedor: '',
    tipo: 'prestamo',           // prestamo | libranza | personal | fiado | otro
    cuenta_id: null,
    monto_original: 0,
    saldo_pendiente: 0,
    tasa_ea: null,
    periodicidad: 'mensual',
    cuotas_totales: 0,
    cuotas_pagadas: 0,
    valor_cuota: 0,
    sistema_amortizacion: 'frances',   // frances (cuota fija) | aleman (abono fijo)
    dia_pago: 1,
    proxima_fecha_pago: null,
    en_mora: false,
    dias_mora: 0,
    me_deben: false,            // true = es plata que te deben a ti

    // ── Ciclo de vida ──
    // `fecha_prestamo` NO es lo mismo que `actualizado_en`: es el día en que
    // salió la plata. Sin este campo no se puede decir "te debe hace 45 días",
    // que es justo el dato que hace acordarse de cobrar.
    fecha_prestamo: hoy(),
    estado: ESTADO_DEUDA.ACTIVA,
    activa: true,               // espejo de estado, para el índice de Dexie
    total_abonado: 0,           // suma de lo recibido o pagado
    monto_condonado: 0,         // lo que se perdonó al cerrar
    saldo_a_favor: 0,           // si pagaron de más y se dejó a cuenta
    cerrada_en: null,           // 'YYYY-MM-DD'
    motivo_cierre: '',
    notas: '',
    ...datos,
  };
}

/** Qué clase de evento es una fila del historial. */
export const CLASE_ABONO = {
  PRESTAMO: 'prestamo',        // la fila cero: el día que salió la plata
  ABONO: 'abono',              // un pago parcial o total
  SOBREPAGO: 'sobrepago',      // pagaron de más
  CONDONACION: 'condonacion',  // se cerró perdonando el resto
  AJUSTE: 'ajuste',            // corrección manual del saldo
};

/**
 * Un renglón del historial de una deuda o de una compra a cuotas.
 *
 * ── POR QUÉ SE GUARDA EL SALDO EN CADA FILA ─────────────────────────────────
 *
 * Se podría recalcular sumando todos los abonos. Se guarda igual, a propósito:
 * si mañana se edita el monto original o se borra un abono, el historial
 * seguiría contando lo que pasó de verdad ese día. Un extracto bancario hace
 * exactamente esto, y por la misma razón.
 */
export function crearAbono(datos = {}) {
  return {
    ...control(),
    deuda_id: null,             // → deudas
    compra_id: null,            // → compras_diferidas
    movimiento_id: null,        // el movimiento que mueve la plata de verdad
    clase: CLASE_ABONO.ABONO,
    fecha: hoy(),
    monto: 0,                   // siempre positivo
    cuenta_id: null,            // de dónde salió o a dónde entró
    metodo_pago: METODO_PAGO.TRANSFERENCIA,
    saldo_antes: 0,
    saldo_despues: 0,
    cubre_cuotas: [],           // números de cuota que tapó, si aplica
    proxima_fecha_pago: null,   // lo que quedaron de acordar ESE día
    nota: '',
    ...datos,
  };
}

/** Un evento de la agenda, con su presupuesto. */
export function crearEvento(datos = {}) {
  return {
    ...control(),
    titulo: '',
    fecha_inicio: hoy(),
    fecha_fin: null,
    hora_inicio: null,
    lugar: '',
    tipo: 'personal',           // personal | trabajo | viaje | familiar

    // Presupuesto desglosado
    presupuesto_gasolina: 0,
    presupuesto_peajes: 0,
    presupuesto_comida: 0,
    presupuesto_hospedaje: 0,
    presupuesto_otros: 0,
    limite_gasto: 0,            // el tope duro; la app avisa al acercarse

    // Para calcular la gasolina
    kilometros: null,
    rendimiento_km_galon: null, // lo del vehículo
    precio_galon: null,

    completado: false,
    notas: '',
    ...datos,
  };
}

/**
 * Una suscripción o un pago fijo: Netflix, el gimnasio, el plan del celular.
 *
 * Cómo se cobra decide todo lo demás (ver lib/recurrentes.js):
 *   tarjeta            se cobra sola a una tarjeta de crédito → se anota sola
 *   debito_automatico  se debita sola de una cuenta → se anota sola, con 4x1000
 *   manual             la pagas tú → la app recuerda, tú confirmas
 */
export function crearRecurrente(datos = {}) {
  return {
    ...control(),
    nombre: '',                 // "Netflix", "Gimnasio"
    icono: null,
    monto: 0,                   // lo que cobra cada vez, en pesos
    monto_variable: false,      // en dólares o por consumo: cambia cada vez
    frecuencia: 'mensual',      // semanal | quincenal | mensual | bimestral | trimestral | semestral | anual
    fecha_inicio: hoy(),        // el primer cobro de verdad (si hay prueba gratis, cuando termina)
    registrada_en: hoy(),       // desde aquí se anotan cobros: no se inventa el pasado
    cobro: 'manual',            // tarjeta | debito_automatico | manual
    cuenta_id: null,            // la tarjeta o la cuenta de donde sale
    metodo_pago: 'efectivo',    // si es manual: cómo la pagas normalmente
    categoria_id: null,

    prueba_gratis: false,       // hasta fecha_inicio no cobra
    pausada_desde: null,        // el gym en vacaciones
    pausada_hasta: null,
    cancelada_en: null,         // desde este día ya no cobra
    saltados: [],               // ciclos que no se cobraron ("este mes no")
    avisar_dias_antes: 3,
    notas: '',

    // De la primera versión de la tabla. Se conservan para no romper datos.
    plantilla: {},
    dia_del_mes: null,
    proxima_fecha: null,
    ultima_generada: null,
    crear_automatico: false,
    activa: true,
    ...datos,
  };
}

/** Regla de categorización automática, que aprende de tus correcciones. */
export function crearRegla(datos = {}) {
  return {
    ...control(),
    patron: '',                 // texto que se busca en concepto o contraparte
    campo: 'contraparte',
    categoria_id: null,
    metodo_pago: null,
    cuenta_id: null,
    veces_aplicada: 0,
    creada_por_usuario: true,
    ...datos,
  };
}

/** Catálogo de bancos y billeteras, con pistas para reconocerlos en una foto. */
export function crearEntidad(datos = {}) {
  return {
    ...control(),
    nombre: '',
    tipo: 'banco',              // banco | billetera | neobanco | cooperativa
    alias: [],                  // cómo aparece escrito en los comprobantes
    color: null,
    cobra_gmf: true,
    ...datos,
  };
}

/**
 * Un contrato de prestación de servicios.
 *
 * ── POR QUÉ ESTO MERECE SU PROPIA TABLA ─────────────────────────────────────
 *
 * Un contratista no tiene empleador que le descuente nada. Cada mes tiene que
 * acordarse solo de: cuánto le entra, cuánto apartar para seguridad social,
 * y ANTES DE QUÉ DÍA pagarla. Ese último dato no lo sabe casi nadie, porque
 * depende de los dos últimos dígitos de la cédula.
 *
 * Guardando el contrato una vez, la app puede calcular todo eso sola y
 * avisar a tiempo, en vez de que uno lo descubra cuando ya está en mora.
 */
export function crearContrato(datos = {}) {
  return {
    ...control(),
    nombre: '',                  // "Asamblea de Córdoba 2026"
    contratante: '',             // quién te paga
    numero: '',                  // el número del contrato
    objeto: '',                  // qué te contrataron a hacer

    valor_total: 0,
    valor_mensual: 0,
    fecha_inicio: hoy(),
    fecha_fin: null,

    // Para calcular la fecha límite de pago de la seguridad social.
    // Solo se guardan los DOS ÚLTIMOS dígitos: es lo único que hace falta
    // para el calendario, y guardar la cédula entera sería pedir un dato
    // sensible sin necesidad.
    cedula_ultimos_2: '',

    nivel_riesgo_arl: 1,         // 1 = oficina. Sube si el trabajo es riesgoso.

    // Descuentos que le hacen a uno en contratos con entidades públicas.
    retencion_pct: null,         // retención en la fuente
    estampillas_pct: null,       // estampillas departamentales

    cuenta_id: null,             // dónde te consignan
    dia_pago_esperado: null,     // más o menos cuándo te pagan

    activo: true,
    notas: '',
    ...datos,
  };
}

export function crearAjuste(clave, valor) {
  return { ...control(), clave, valor };
}

// ─── Utilidades de fecha ────────────────────────────────────────────────────
// Se guardan como texto 'YYYY-MM-DD' y no como Date, por dos razones: se leen
// igual en la hoja de cálculo, y no cambian de día según la zona horaria.

export function hoy() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function mesActual() {
  return hoy().slice(0, 7);
}

/** Marca un registro como borrado sin quitarlo, para que el otro dispositivo se entere. */
export function marcarBorrado(registro) {
  return {
    ...registro,
    borrado_en: Date.now(),
    actualizado_en: Date.now(),
    dispositivo: idDispositivo(),
  };
}

/** Sella un registro como modificado ahora. Úsalo en TODA escritura. */
export function tocar(registro) {
  return {
    ...registro,
    actualizado_en: Date.now(),
    dispositivo: idDispositivo(),
  };
}
